import React, { useEffect, useState } from "react";
import { Label, Row, Col } from "reactstrap";
import Select, { OptionsType } from "react-select";
import { Box, X } from "react-feather";
import Button from "../../../../components/common/button";
import commonService, { CommonService } from "../../../../services/commonService";
import { uniqBy } from "../../../../utils/array";
import companyService from "../../../../services/companyService";
import { connect } from "react-redux";
import { IApplicationState } from "../../../../redux/reducers";
import userService from "../../../../services/userService";
import { ICompany } from "../../../../interfaces/company";
import PermissionNotice from "../../permissionNotice";
import { confirmDelete } from "../../../../utils/confirmation";

interface IProps {
	company: any | undefined;
	getUserProfile: () => Promise<any>;
	saveMarkets: (markets: { id: string }[]) => Promise<any>;
	removeMarket: (id: string) => Promise<any>;
	role?: any;
}
const Markets: React.FC<IProps> = ({
	company,
	getUserProfile,
	saveMarkets,
	removeMarket,
	role
}) => {
	const [markets, setMarkets] = useState<any>([]);
	const [loading, setLoading] = useState<boolean>();
	const [selected, setSelected] = useState<string[]>([]);
	const [hasInitialized, setHasInitialized] = useState<boolean>(false);

	useEffect(() => {
		setLoading(true);
		Promise.all([
			commonService.getAllMarkets().then((res: any) => {
				// Handle both raw array and { data: [] } response formats
				const data = Array.isArray(res) ? res : (res?.data || []);
				setMarkets(data);
			}),
			getUserProfile()
		]).finally(() => {
			setLoading(false);
		});
	}, []);

	useEffect(() => {
		// Initialize only once when both sources are ready
		if (company && markets.length > 0 && !hasInitialized) {
			const initialIds = (company.markets || []).map((m: any) => (typeof m === "string" ? m : m.id));
			setSelected(initialIds);
			setHasInitialized(true);
		}
	}, [company, markets, hasInitialized]);

	const onSelectTypes = (option: any) => {
		if (!option || !option.value) return;
		const newId = option.value.id;
		if (newId && !selected.includes(newId)) {
			setSelected([...selected, newId]);
		}
	};

	const marketOptions = () => {
		return markets
			.filter((m: any) => !selected.includes(m.id))
			?.map((market: any) => {
				return {
					label: market.name,
					value: market
				};
			});
	};

	const onSave = () => {
		setLoading(true);
		const formatted = selected.map((id: string) => ({ id: id }));
		saveMarkets(formatted)
			.then(
				getUserProfile // Update user profile in store
			)
			.finally(() => {
				setLoading(false);
			});
	};

	const onRemoveMarket = (market: any) => {
		confirmDelete(market.name, "Market", () => {
			setSelected(selected.filter((id: any) => id !== market.id));
			setLoading(true);
			removeMarket(market.id)
				.then(getUserProfile)
				.finally(() => {
					setLoading(false);
				});
		});
	};

	const renderedCompanyMarkets = () => {
		const companyMarkets = markets.filter((m: any) => selected.includes(m.id));
		return companyMarkets.map((m: any) => {
			return (
				<span key={m.id} className="market-tag">
					<span className="remove-pref" onClick={() => onRemoveMarket(m)}>
						<X size={12} />
					</span>
					{m.name}
				</span>
			);
		});
	};

	return (
		<>
			{role !== "COMPANY_USER" ? (
				<div>
					<div
						className={"market-pref"}
						style={{ borderBottom: "1px dashed #cedbe9", paddingBottom: 15, paddingTop: 11 }}
					>
						<Row style={{ marginBottom: 12 }}>
							<Col>
								{/* <Label for="Markets" className="color-battleship-grey">Ingredient Supplier</Label> */}
								<Select
									styles={{
										menu: (provided) => ({
											...provided,
											zIndex: 9999
										}),
										menuPortal: (base) => ({
											...base,
											zIndex: 9999
										})
									}}
									menuPortalTarget={document.body}
									name="Markets"
									options={marketOptions()}
									isDisabled={loading}
									onChange={onSelectTypes}
									isSearchable={true}
									placeholder="Select markets..."
									controlShouldRenderValue={false}
								/>
							</Col>
						</Row>
						<Row className={"mt-3"}>
							<div className="selected-markets-grid">
								{renderedCompanyMarkets()}
							</div>
						</Row>
					</div>
					<div style={{ paddingTop: 17 }}>
						<p>
							Choosing your markets will help tailor our product suggestions and filter your <br />
							live searching based on regional relevance whilst using the app.
						</p>
						<Button
							className="btn btn-primary color-white ls-5 font-weight-semibold mt-3"
							type="submit"
							onClick={onSave}
						>
							Save account settings
						</Button>
					</div>
				</div>
			) : (
				<PermissionNotice />
			)}
		</>
	);
};

const mapStateToProps = (state: IApplicationState) => ({
	company: state.user.profile?.company,
	role: state.user?.profile?.role
});

const mapDispatchToProps = {
	getUserProfile: () => userService.getUserProfile(),
	saveMarkets: (markets: { id: string }[]) => companyService.updateCompanyMarkets(markets),
	removeMarket: (id: string) => companyService.removeCompanyMarket(id)
};

export default connect(mapStateToProps, mapDispatchToProps)(Markets);
